import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './OffersPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

function StatusBadge({ enabled }) {
  return (
    <span className={`offers-status ${enabled ? 'is-live' : 'is-off'}`}>
      {enabled ? 'Live' : 'Disabled'}
    </span>
  );
}

function CategoryPills({ labels }) {
  if (!labels?.length) return <span className="offers-muted">None</span>;
  return (
    <div className="offers-pills">
      {labels.map((label) => (
        <span key={label} className="offers-pill">{label}</span>
      ))}
    </div>
  );
}

export default function OffersPage() {
  const { currentUser, isAdmin } = useAuth();
  const [offers, setOffers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settingsMessage, setSettingsMessage] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [productJson, setProductJson] = useState('');
  const [cartJson, setCartJson] = useState('');

  const authedFetch = useCallback(async (url, options = {}) => {
    const token = await currentUser.getIdToken();
    return fetch(`${API_BASE}${url}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const loadOffers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authedFetch('/api/v1/offers/current');
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to load offers.');
      }
      setOffers(data.offers || null);
      if (data.offers?.rawConfig) {
        setProductJson(JSON.stringify(data.offers.rawConfig.productDiscount || {}, null, 2));
        setCartJson(JSON.stringify(data.offers.rawConfig.cartDiscount || {}, null, 2));
      }
    } catch (err) {
      setError(err.message || 'Failed to load offers.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const rawJson = useMemo(() => {
    if (!offers?.rawConfig) return '';
    return JSON.stringify(offers.rawConfig, null, 2);
  }, [offers]);

  const saveSettings = useCallback(async () => {
    setSavingSettings(true);
    setSettingsMessage(null);
    try {
      const productDiscount = JSON.parse(productJson);
      const cartDiscount = JSON.parse(cartJson);
      const res = await authedFetch('/api/v1/offers/current', {
        method: 'POST',
        body: JSON.stringify({ productDiscount, cartDiscount }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to save offers.');
      }
      setOffers(data.offers || null);
      if (data.offers?.rawConfig) {
        setProductJson(JSON.stringify(data.offers.rawConfig.productDiscount || {}, null, 2));
        setCartJson(JSON.stringify(data.offers.rawConfig.cartDiscount || {}, null, 2));
      }
      setSettingsMessage({ type: 'success', text: 'Offer settings saved.' });
    } catch (err) {
      setSettingsMessage({ type: 'error', text: err.message || 'Invalid JSON or save failed.' });
    } finally {
      setSavingSettings(false);
    }
  }, [authedFetch, productJson, cartJson]);

  if (loading) {
    return <div className="offers-page"><div className="offers-loading">Loading current offers...</div></div>;
  }

  const productDiscount = offers?.productDiscount || {};
  const cartDiscount = offers?.cartDiscount || {};

  return (
    <div className="offers-page">
      <div className="offers-header">
        <div>
          <h1>Current Offers</h1>
          <p>Reference for customer support and offline order discount decisions.</p>
        </div>
        <button type="button" className="offers-refresh" onClick={loadOffers}>Refresh</button>
      </div>

      {error ? <div className="offers-alert">{error}</div> : null}

      <section className="offers-section">
        <div className="offers-section-header">
          <div>
            <span className="offers-kicker">Product Level Discount</span>
            <h2>{productDiscount.name || 'Product discount'}</h2>
          </div>
          <StatusBadge enabled={productDiscount.enabled !== false} />
        </div>
        <p className="offers-summary">{productDiscount.summary}</p>

        <div className="offers-grid">
          <div className="offers-panel">
            <h3>Meter Pricing Rule</h3>
            <div className="offers-table-wrap">
              <table className="offers-table">
                <thead>
                  <tr>
                    <th>Purchased Meter</th>
                    <th>Discount</th>
                  </tr>
                </thead>
                <tbody>
                  {(productDiscount.tiers || []).map((tier) => (
                    <tr key={tier.meterRange}>
                      <td>{tier.meterRange}</td>
                      <td>{tier.discount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="offers-panel">
            <h3>Eligible Products</h3>
            <CategoryPills labels={productDiscount.eligibleProductTypes || []} />
            {productDiscount.eligibleProductIds?.length ? (
              <>
                <h3 className="offers-subtitle">Eligible Product IDs</h3>
                <CategoryPills labels={productDiscount.eligibleProductIds} />
              </>
            ) : (
              <p className="offers-note">No specific product IDs are configured, so eligibility is controlled by product type.</p>
            )}
          </div>
        </div>
      </section>

      <section className="offers-section">
        <div className="offers-section-header">
          <div>
            <span className="offers-kicker">Cart Level BXGY Discount</span>
            <h2>{cartDiscount.name || 'Cart discount'}</h2>
          </div>
          <StatusBadge enabled />
        </div>
        <p className="offers-summary">{cartDiscount.summary}</p>
        <p className="offers-note">{cartDiscount.basis}</p>

        <div className="offers-table-wrap">
          <table className="offers-table offers-table-wide">
            <thead>
              <tr>
                <th>Buy Meters</th>
                <th>Free Meters</th>
                <th>Free Fabric Categories</th>
              </tr>
            </thead>
            <tbody>
              {(cartDiscount.tiers || []).map((tier) => (
                <tr key={`${tier.buyMeters}-${tier.freeMeters}`}>
                  <td>{tier.buyMeters}m</td>
                  <td>{tier.freeMeters}m free</td>
                  <td><CategoryPills labels={tier.rewardCategoryLabels || tier.rewardCategories || []} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {isAdmin && rawJson ? (
        <details className="offers-settings">
          <summary>Settings JSON</summary>
          <p>Admin-only editable JSON. Saved settings override the default code config.</p>
          <div className="offers-settings-meta">
            <span>Source: <strong>{offers.source || 'backend config'}</strong></span>
            {offers.updatedAt ? <span>Updated: <strong>{new Date(offers.updatedAt).toLocaleString()}</strong></span> : null}
            {offers.updatedBy ? <span>By: <strong>{offers.updatedBy}</strong></span> : null}
          </div>
          {settingsMessage ? (
            <div className={`offers-settings-alert is-${settingsMessage.type}`}>{settingsMessage.text}</div>
          ) : null}
          <label className="offers-json-field">
            Product discount JSON
            <textarea
              value={productJson}
              onChange={(e) => setProductJson(e.target.value)}
              spellCheck={false}
            />
          </label>
          <label className="offers-json-field">
            Cart BXGY discount JSON
            <textarea
              value={cartJson}
              onChange={(e) => setCartJson(e.target.value)}
              spellCheck={false}
            />
          </label>
          <div className="offers-settings-actions">
            <button type="button" className="offers-refresh" onClick={saveSettings} disabled={savingSettings}>
              {savingSettings ? 'Saving...' : 'Save JSON'}
            </button>
          </div>
        </details>
      ) : null}
    </div>
  );
}
